'use client';

import { subscribeToMiniCartAnnouncements } from '@lib/commerce/miniCartAnnouncement';
import { useCallback, useEffect, useRef, useState } from 'react';

export type MiniCartAnnouncement = {
  /** Changes with every announcement, so a repeated message is re-read. */
  id: number;
  message: string;
};

/**
 * What the header's mini-cart live region says: the cart count whenever it
 * changes after first appearing, and whatever the island announces (its
 * errors). A count hidden while rate limited is neither news nor a reset.
 */
export const useMiniCartAnnouncement = (
  count: number | undefined,
  formatCount: (count: number) => string
): MiniCartAnnouncement | null => {
  const [announcement, setAnnouncement] = useState<MiniCartAnnouncement | null>(
    null
  );
  const announce = useCallback((message: string) => {
    setAnnouncement((previous) => ({ id: (previous?.id ?? 0) + 1, message }));
  }, []);

  const formatCountRef = useRef(formatCount);
  formatCountRef.current = formatCount;
  const lastCountRef = useRef(count);
  useEffect(() => {
    if (count === undefined) return;
    const lastCount = lastCountRef.current;
    lastCountRef.current = count;
    if (lastCount !== undefined && lastCount !== count) {
      announce(formatCountRef.current(count));
    }
  }, [count, announce]);

  useEffect(() => subscribeToMiniCartAnnouncements(announce), [announce]);

  return announcement;
};
